import { Injectable } from '@angular/core';
import { getDatabase, ref, set, get, Database } from 'firebase/database';
import { getAuth, onAuthStateChanged, User } from 'firebase/auth';
import { BehaviorSubject, Observable } from 'rxjs';
import { ReasoningLevel } from './reasoning.service';

export interface UserPreferences {
  reasoningLevels?: { [modelId: string]: ReasoningLevel };
  [key: string]: any;
}

@Injectable({ providedIn: 'root' })
export class UserPreferencesService {
  private db: Database;
  private auth = getAuth();
  private currentUser$ = new BehaviorSubject<User | null>(null);
  private preferences$ = new BehaviorSubject<UserPreferences>({});

  constructor() {
    this.db = getDatabase();
    
    // Listen to auth state changes
    onAuthStateChanged(this.auth, (user) => {
      this.currentUser$.next(user);
      if (user) {
        this.loadPreferences(user.uid);
      } else {
        this.preferences$.next({});
      }
    });
  }

  /**
   * Load user preferences from Firebase
   */
  private async loadPreferences(uid: string): Promise<void> {
    try {
      const prefRef = ref(this.db, `userPreferences/${uid}`);
      const snapshot = await get(prefRef);
      const preferences = snapshot.exists() ? snapshot.val() : {};
      this.preferences$.next(preferences);
    } catch (error) {
      console.error('Error loading preferences:', error);
      this.preferences$.next({});
    }
  }

  /**
   * Get reasoning level for a specific model
   */
  async getReasoningLevelForModel(modelId: string): Promise<ReasoningLevel | null> {
    const user = this.currentUser$.value;
    if (!user) return null;

    try {
      const prefRef = ref(this.db, `userPreferences/${user.uid}/reasoningLevels/${modelId}`);
      const snapshot = await get(prefRef);
      return snapshot.exists() ? snapshot.val() : null;
    } catch (error) {
      console.error('Error getting reasoning level:', error);
      return null;
    }
  }

  /**
   * Save reasoning level for a specific model
   */
  async saveReasoningLevelForModel(modelId: string, level: ReasoningLevel): Promise<void> {
    const user = this.currentUser$.value;
    if (!user) {
      // If not logged in, save to localStorage as fallback
      const localPrefs = JSON.parse(localStorage.getItem('userPreferences') || '{}');
      if (!localPrefs.reasoningLevels) localPrefs.reasoningLevels = {};
      localPrefs.reasoningLevels[modelId] = level;
      localStorage.setItem('userPreferences', JSON.stringify(localPrefs));
      return;
    }

    try {
      const prefRef = ref(this.db, `userPreferences/${user.uid}/reasoningLevels/${modelId}`);
      await set(prefRef, level);
      
      // Update local preferences
      const currentPrefs = this.preferences$.value;
      if (!currentPrefs.reasoningLevels) currentPrefs.reasoningLevels = {};
      currentPrefs.reasoningLevels[modelId] = level;
      this.preferences$.next(currentPrefs);
    } catch (error) {
      console.error('Error saving reasoning level:', error);
      throw error;
    }
  }

  /**
   * Get all preferences as Observable
   */
  getPreferences$(): Observable<UserPreferences> {
    return this.preferences$.asObservable();
  }

  /**
   * Get current preferences synchronously
   */
  getPreferences(): UserPreferences {
    return this.preferences$.value;
  }

  /**
   * Get current user
   */
  getCurrentUser(): User | null {
    return this.currentUser$.value;
  }
}
